import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  Participant,
  CreateParticipantDto,
  UpdateParticipantDto,
} from "@autfit/shared-types";
import { participantsAPI } from "../../services/api";

interface ParticipantsState {
  participants: Participant[];
  currentParticipant: Participant | null;
  uniqueClasses: string[];
  isLoading: boolean;
  isLoadingClasses: boolean;
  error: string | null;
  pagination: {
    page: number;
    pageSize: number;
    totalPages: number;
    totalItems: number;
  };
}

const initialState: ParticipantsState = {
  participants: [],
  currentParticipant: null,
  uniqueClasses: [],
  isLoading: false,
  isLoadingClasses: false,
  error: null,
  pagination: {
    page: 1,
    pageSize: 10,
    totalPages: 0,
    totalItems: 0,
  },
};

// Async thunks
export const fetchParticipants = createAsyncThunk(
  "participants/fetchParticipants",
  async (
    {
      page = 1,
      pageSize = 10,
      search,
      className,
    }: {
      page?: number;
      pageSize?: number;
      search?: string;
      className?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await participantsAPI.getAll(
        page,
        pageSize,
        search,
        className,
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch participants",
      );
    }
  },
);

export const fetchParticipantById = createAsyncThunk(
  "participants/fetchParticipantById",
  async (id: string, { rejectWithValue }) => {
    try {
      const participant = await participantsAPI.getById(id);
      return participant;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch participant",
      );
    }
  },
);

export const createParticipant = createAsyncThunk(
  "participants/createParticipant",
  async (data: CreateParticipantDto, { rejectWithValue }) => {
    try {
      const participant = await participantsAPI.create(data);
      return participant;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create participant",
      );
    }
  },
);

export const updateParticipant = createAsyncThunk(
  "participants/updateParticipant",
  async (
    { id, data }: { id: string; data: UpdateParticipantDto },
    { rejectWithValue },
  ) => {
    try {
      const participant = await participantsAPI.update(id, data);
      return participant;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update participant",
      );
    }
  },
);

export const deleteParticipant = createAsyncThunk(
  "participants/deleteParticipant",
  async (id: string, { rejectWithValue }) => {
    try {
      await participantsAPI.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete participant",
      );
    }
  },
);

export const fetchUniqueClasses = createAsyncThunk(
  "participants/fetchUniqueClasses",
  async (_, { rejectWithValue }) => {
    try {
      const classes = await participantsAPI.getUniqueClasses();
      return classes;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch classes",
      );
    }
  },
);

const participantsSlice = createSlice({
  name: "participants",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentParticipant: (state) => {
      state.currentParticipant = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch participants
      .addCase(fetchParticipants.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchParticipants.fulfilled, (state, action) => {
        state.isLoading = false;
        state.participants = action.payload.items;
        state.pagination = {
          page: action.payload.page,
          pageSize: action.payload.pageSize,
          totalPages: Math.ceil(action.payload.total / action.payload.pageSize),
          totalItems: action.payload.total,
        };
        state.error = null;
      })
      .addCase(fetchParticipants.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Fetch participant by ID
      .addCase(fetchParticipantById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchParticipantById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentParticipant = action.payload;
        state.error = null;
      })
      .addCase(fetchParticipantById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Create participant
      .addCase(createParticipant.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createParticipant.fulfilled, (state, action) => {
        state.isLoading = false;
        state.participants.unshift(action.payload);
        state.pagination.totalItems += 1;
        state.error = null;
      })
      .addCase(createParticipant.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Update participant
      .addCase(updateParticipant.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateParticipant.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.participants.findIndex(
          (p) => p.id === action.payload.id,
        );
        if (index !== -1) {
          state.participants[index] = action.payload;
        }
        if (state.currentParticipant?.id === action.payload.id) {
          state.currentParticipant = action.payload;
        }
        state.error = null;
      })
      .addCase(updateParticipant.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Delete participant
      .addCase(deleteParticipant.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteParticipant.fulfilled, (state, action) => {
        state.isLoading = false;
        state.participants = state.participants.filter(
          (p) => p.id !== action.payload,
        );
        if (state.currentParticipant?.id === action.payload) {
          state.currentParticipant = null;
        }
        state.pagination.totalItems -= 1;
        state.error = null;
      })
      .addCase(deleteParticipant.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Fetch unique classes
      .addCase(fetchUniqueClasses.pending, (state) => {
        state.isLoadingClasses = true;
      })
      .addCase(fetchUniqueClasses.fulfilled, (state, action) => {
        state.isLoadingClasses = false;
        state.uniqueClasses = action.payload;
      })
      .addCase(fetchUniqueClasses.rejected, (state, action) => {
        state.isLoadingClasses = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearCurrentParticipant } =
  participantsSlice.actions;
export default participantsSlice.reducer;
